import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag, Heart } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { formatAUD } from "@/lib/media";
import { Paw } from "@/components/decor";
import { toast } from "sonner";
import CartBundleNudge from "@/components/CartBundleNudge";
import CutoffNotice from "@/components/CutoffNotice";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, removeFromCart, updateQty, saveForLater, subtotal, cartCount } = useStore();
  const navigate = useNavigate();

  const go = (path) => { setCartOpen(false); navigate(path); };

  const handleSave = (item) => {
    saveForLater(item);
    toast.success(`${item.name} moved to your wishlist 💛`);
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#FFF9EE] p-0 flex flex-col" data-testid="cart-drawer">
        <SheetTitle className="sr-only">Your shopping bag</SheetTitle>
        <div className="p-5 border-b border-[#19324A]/10 flex items-center justify-between bg-white">
          <h3 className="font-display font-bold text-xl text-[#19324A] flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Your Bag ({cartCount})
          </h3>
          <button onClick={() => setCartOpen(false)} aria-label="Close" className="p-1 text-[#19324A]/60"><X className="w-6 h-6" /></button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
            <Paw className="w-16 h-16 text-[#FFD93D]" />
            <p className="font-display font-semibold text-lg text-[#19324A]">Your shopping bag is looking a little lonely. 🐾</p>
            <button onClick={() => go("/shop")} className="btn-primary" data-testid="empty-cart-shop-btn">Go Sniffing for Goodies</button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {cart.map((item) => (
                <div key={item.key} className="flex gap-3 bg-white rounded-2xl p-3 soft-shadow" data-testid={`cart-item-${item.slug}`}>
                  <img src={item.image} alt={item.name} className="w-20 h-20 rounded-xl object-cover bg-[#FFF9EE]" />
                  <div className="flex-1 min-w-0">
                    {item.type === "bundle" ? (
                      <p className="font-display font-semibold text-[#19324A] text-sm leading-snug">{item.name}</p>
                    ) : (
                      <Link to={`/product/${item.slug}`} onClick={() => setCartOpen(false)} className="font-display font-semibold text-[#19324A] text-sm leading-snug line-clamp-2 hover:text-[#FF6B9D]">{item.name}</Link>
                    )}
                    {item.type === "bundle" ? (
                      <ul className="text-xs text-[#19324A]/55 mt-0.5 space-y-0.5">
                        {item.components.map((c, i) => <li key={i}>• {c.name}{(c.colour || c.size) ? ` (${[c.colour, c.size].filter(Boolean).join(", ")})` : ""}</li>)}
                      </ul>
                    ) : (item.size || item.colour || item.personalisation) && (
                      <p className="text-xs text-[#19324A]/55 mt-0.5">{[item.colour, item.size].filter(Boolean).join(" · ")}{item.personalisation && <span className="text-[#FF6B9D] font-semibold" data-testid="cart-personalisation">{(item.colour || item.size) ? " · " : ""}“{item.personalisation}”</span>}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 bg-[#FFF9EE] rounded-full px-1">
                        <button onClick={() => updateQty(item.key, item.quantity - 1)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white" aria-label="Decrease"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-5 text-center text-sm font-semibold">{item.quantity}</span>
                        <button onClick={() => updateQty(item.key, item.quantity + 1)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white" aria-label="Increase"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <span className="font-display font-bold text-[#19324A]">{formatAUD(item.price * item.quantity)}</span>
                    </div>
                    {item.type !== "bundle" && (
                      <button onClick={() => handleSave(item)} data-testid={`cart-save-later-${item.slug}`}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-display font-semibold text-[#19324A]/60 hover:text-[#FF6B9D] transition-colors">
                        <Heart className="w-3.5 h-3.5" /> Save for later
                      </button>
                    )}
                  </div>
                  <button onClick={() => removeFromCart(item.key)} aria-label="Remove" data-testid={`cart-remove-${item.slug || item.key}`} className="text-[#19324A]/40 hover:text-[#FF6B9D] self-start"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <CartBundleNudge />
            </div>

            <div className="border-t border-[#19324A]/10 p-5 bg-white space-y-3">
              {cart.some((i) => i.personalisation) && <div className="flex justify-center"><CutoffNotice compact /></div>}
              <p className="text-xs text-center text-[#19324A]/60">🐾 Shipping & taxes calculated at checkout.</p>
              <div className="flex items-center justify-between font-display font-bold text-lg text-[#19324A]">
                <span>Subtotal</span><span data-testid="cart-subtotal">{formatAUD(subtotal)}</span>
              </div>
              <button onClick={() => go("/checkout")} className="btn-pink w-full" data-testid="cart-checkout-btn">Checkout</button>
              <button onClick={() => go("/cart")} className="btn-outline w-full !py-2.5" data-testid="cart-view-btn">View Bag</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
